import { computed, getCurrentInstance, onMounted, ref, toRef, watch } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import type { Route } from 'vue-router';
import { TRIAGE_ORDER } from '@/features/overall-assessment/constants/triage.constants';
import { useOverallAssessmentStore } from '@/features/overall-assessment/stores/overallAssessment';
import { useMrxStore } from '@/stores/mrx';
import { useTrackStore } from '@/stores/track';
import { assess as runAssess, useDeviceMRX } from '@/utils/mrxEngine';
import type { AssessedCondition, ContentItem, DisabledItem, Triage } from '../types/overall-assessment.types';

type Next = () => void;

interface ClinicalTrack {
  name?: string;
}

interface AnalyticsProxy {
  $online?: boolean;
  $track: (event: string, payload?: Record<string, unknown>) => void;
  $analyticsEvents: Record<string, string>;
  $te: (token: string) => boolean;
  icon: (triage: Triage) => { color: string };
  riskAssessmentToken: (name: string, severity: string) => string;
}

export interface TriageSection {
  triage: Triage;
  headerToken: string;
  headerIconColor: string;
  contentList: ContentItem[];
  disabledItems: Record<string, DisabledItem>;
  initiallyCollapsed: boolean;
}

export interface RiskAssessmentWorkflow {
  loading: Ref<boolean>;
  valid: Ref<boolean | null>;
  overallTriage: Ref<Triage>;
  conditions: Ref<AssessedCondition[]>;
  showPreTriageDialog: ComputedRef<boolean>;
  selected: Ref<string[]>;
  message: ComputedRef<string>;
  notify: ComputedRef<boolean>;
  connectionLost: ComputedRef<boolean>;
  triageSections: ComputedRef<TriageSection[]>;
  assess: () => Promise<void>;
  retry: () => Promise<void>;
  acknowledgeTriage: () => void;
  setSelected: (selected: string[]) => void;
  handleBeforeRouteLeave: (to: Route, from: Route, next: Next) => void;
}

export function useRiskAssessmentWorkflow(): RiskAssessmentWorkflow {
  const vm = getCurrentInstance();
  const proxy = vm?.proxy as unknown as AnalyticsProxy | undefined;

  const loading = ref(true);
  const valid = ref<boolean | null>(null);
  const overallTriage = ref<Triage>('none');
  const conditions = ref<AssessedCondition[]>([]);
  const preTriageDialogOpen = ref(true);
  const overallAssessmentStore = useOverallAssessmentStore();
  const selected = toRef(overallAssessmentStore, 'riskAssessmentSelection');
  const activeTab = toRef(overallAssessmentStore, 'activeTab');

  const normalizeTriage = (value: unknown): Triage => {
    return TRIAGE_ORDER.includes(value as Triage) ? (value as Triage) : 'none';
  };

  const mrxStore = useMrxStore();

  const notification = computed(() => mrxStore.notification);
  const validAssessment = computed(() => Boolean(mrxStore.valid));
  const triageFromBundle = computed<Triage>(() => normalizeTriage(mrxStore.triage));
  const trackStore = useTrackStore();
  const filterConditions = computed(() => trackStore.filterConditions || []);
  const currentTrack = computed(() => trackStore.current as ClinicalTrack | undefined);

  const message = computed(() => `app.notifications.${notification.value}.title`);
  const notify = computed(() => notification.value !== null);
  const connectionLost = computed(() => !useDeviceMRX() && proxy?.$online === false);
  const showPreTriageDialog = computed(() => preTriageDialogOpen.value
    && !loading.value
    && valid.value === true
    && activeTab.value === 'Risk Assessment');

  const conditionsWithTriageMatch = (triage: Triage): AssessedCondition[] => {
    return conditions.value.filter((condition) => {
      return condition.triage_recommendation === triage && condition.severity !== 'not_assessed';
    });
  };

  const riskAssessmentDetailToken = (name: string, severity: string): string | null => {
    const normalizedSeverity = severity === 'risk_none' ? 'none' : severity;
    const token = `assessment.${name}.${normalizedSeverity}.triage`;

    return proxy?.$te(token) ? token : null;
  };

  const formatTriages = (triage: Triage): ContentItem[] => {
    const conditionsWithTriage = conditionsWithTriageMatch(triage);

    return conditionsWithTriage.map((condition) => {
      const detailToken = riskAssessmentDetailToken(condition.name, condition.severity);

      return {
        name: condition.name,
        headerToken: proxy?.riskAssessmentToken(condition.name, condition.severity)
          || `assessment.${condition.name}.${condition.severity}.title`,
        detailContent: detailToken ? [detailToken] : [],
        triage,
        isNoRisk: triage === 'none',
        expansionKey: `${triage}:${condition.name}:${condition.severity}`,
      };
    });
  };

  const triageSections = computed<TriageSection[]>(() => {
    return TRIAGE_ORDER
      .map((triage) => {
        const contentList = formatTriages(triage);

        return {
          triage,
          headerToken: `triage.${triage}.title`,
          headerIconColor: proxy?.icon(triage).color || 'possible',
          contentList,
          disabledItems: {} as Record<string, DisabledItem>,
          initiallyCollapsed: triage === 'none',
        };
      })
      .filter((section) => section.contentList.length > 0);
  });

  const assess = async (): Promise<void> => {
    const payload = {
      suppressSubmitOnAssessment: true,
      ...(filterConditions.value.length > 0 ? { conditions: filterConditions.value } : {}),
    };

    const assessedEncounter = await runAssess(payload);

    if (validAssessment.value) {
      valid.value = true;
      conditions.value = ((assessedEncounter as AssessedCondition[] | undefined) || []).filter(
        (encounterItem) => encounterItem.type === 'assessment' && !encounterItem.display_hidden,
      );
      overallTriage.value = triageFromBundle.value;

      proxy?.$track(proxy?.$analyticsEvents.TRIAGE_PANEL_VIEWED || 'TRIAGE_PANEL_VIEWED', {
        track_name: currentTrack.value?.name,
        overall_triage: overallTriage.value,
        total_conditions: conditions.value.length,
        conditions_urgent: conditionsWithTriageMatch('urgent').length,
        conditions_immediate: conditionsWithTriageMatch('immediate').length,
        conditions_standard: conditionsWithTriageMatch('standard').length,
        conditions_inconclusive: conditionsWithTriageMatch('inconclusive').length,
        conditions_none: conditionsWithTriageMatch('none').length,
      });

      mrxStore.select({ type: 'assessment', data: selected.value });
      mrxStore.updateCareplanShownList(selected.value);
    } else {
      valid.value = false;
    }

    loading.value = false;
  };

  const retry = async (): Promise<void> => {
    loading.value = true;
    valid.value = null;
    await assess();
  };

  const acknowledgeTriage = (): void => {
    preTriageDialogOpen.value = false;
  };

  const setSelected = (nextSelected: string[]): void => {
    selected.value = nextSelected;
  };

  const handleBeforeRouteLeave = (_to: Route, _from: Route, next: Next): void => {
    if (valid.value && conditions.value.length > 0 && selected.value.length === 0) {
      proxy?.$track(proxy?.$analyticsEvents.TRIAGE_NO_SELECTION || 'TRIAGE_NO_SELECTION', {
        track_name: currentTrack.value?.name,
        overall_triage: overallTriage.value,
        available_conditions: conditions.value.length,
        available_urgent: conditionsWithTriageMatch('urgent').length,
        available_immediate: conditionsWithTriageMatch('immediate').length,
        available_standard: conditionsWithTriageMatch('standard').length,
        available_inconclusive: conditionsWithTriageMatch('inconclusive').length,
      });
    }

    next();
  };

  watch(selected, () => {
    mrxStore.select({ type: 'assessment', data: selected.value });
    mrxStore.updateCareplanShownList(selected.value);
  });

  watch(activeTab, (tab) => {
    if (tab === 'Risk Assessment') preTriageDialogOpen.value = true;
  });

  onMounted(() => {
    assess().then();
  });

  return {
    loading,
    valid,
    overallTriage,
    conditions,
    showPreTriageDialog,
    selected,
    message,
    notify,
    connectionLost,
    triageSections,
    assess,
    retry,
    acknowledgeTriage,
    setSelected,
    handleBeforeRouteLeave,
  };
}

